import React, { useEffect, useRef } from 'react';
import { Chart, registerables, CategoryScale } from 'chart.js';
import { Select, Button } from 'antd';
import { ChartColumnStacked, ChevronDown, Download } from 'lucide-react';
import { tone, StatusTag } from './page';
import { categoryList } from './lists';
import { useShareOfShelf } from './shareOfShelf.controller';
import { brand, neutral, semantic, font } from '../../theme/tokens';
import { t } from '../../company/labels';

Chart.register(...registerables, CategoryScale);

// Chart palette: brand blue first, then info (design system series order). Neutral gridlines.
const MARY_COLOR = brand[600];
const COMPETITION_COLOR = semantic.info.solid;
const GRID_COLOR = '#eef0f3';

export default function ShareOfShelf({ storeChecks, channel, isClientView = false }) {
    const chartRef = useRef(null);
    const { filter, setFilter, isExpanded, setIsExpanded, getChartSeries, download } = useShareOfShelf({ storeChecks, channel, isClientView });

    useEffect(() => {
        let chartInstance = null;

        const { labels, mary, competencia } = getChartSeries();
        // The company's own brand (Mary / marca propia) as the series name.
        const ownBrand = t('ownBrand');

        if (chartRef.current) {
            if (chartRef.current.chart) {
                chartRef.current.chart.destroy();
            }

            chartInstance = new Chart(chartRef.current, {
                type: 'bar',
                data: {
                    labels,
                    datasets: [
                        {
                            data: mary,
                            label: ownBrand,
                            backgroundColor: MARY_COLOR,
                            borderRadius: 4,
                            borderSkipped: false,
                            maxBarThickness: 48
                        },
                        {
                            data: competencia,
                            label: 'Competencia',
                            backgroundColor: COMPETITION_COLOR,
                            borderRadius: 4,
                            borderSkipped: false,
                            maxBarThickness: 48
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        x: {
                            stacked: true,
                            grid: { display: false },
                            border: { color: neutral[200] },
                            ticks: { color: neutral[500], font: { family: font.sans, size: 12 } }
                        },
                        y: {
                            stacked: true,
                            grid: { color: GRID_COLOR },
                            border: { display: false },
                            ticks: { color: neutral[500], font: { family: font.sans, size: 12 } }
                        }
                    },
                    plugins: {
                        legend: {
                            position: 'bottom',
                            labels: {
                                padding: 20,
                                usePointStyle: true,
                                pointStyle: 'circle',
                                boxWidth: 8,
                                boxHeight: 8,
                                color: neutral[600],
                                font: { family: font.sans, size: 12 }
                            }
                        },
                        tooltip: {
                            backgroundColor: neutral[900],
                            titleColor: neutral[0],
                            bodyColor: neutral[200],
                            padding: 10,
                            cornerRadius: 8,
                            titleFont: { family: font.sans, weight: '600' },
                            bodyFont: { family: font.sans },
                            callbacks: {
                                label: ({ dataset, dataIndex }) => {
                                    const maryValue = mary[dataIndex];
                                    const compValue = competencia[dataIndex];
                                    const total = maryValue + compValue;

                                    if (dataset.label === ownBrand) {
                                        const percentMary = ((maryValue / total) * 100).toFixed(2);
                                        return `${ownBrand}: ${percentMary}%`;
                                    } else {
                                        const percentComp = ((compValue / total) * 100).toFixed(2);
                                        return `Competencia: ${percentComp}%`;
                                    }
                                }
                            }
                        }
                    }
                }
            });

            chartRef.current.chart = chartInstance;
        }

        return () => {
            if (chartInstance) chartInstance.destroy();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeChecks, filter, setFilter, isExpanded]);

    return (
        <section className="shelf-card overflow-hidden">
            {/* Header */}
            <button
                type="button"
                aria-expanded={isExpanded}
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left bg-transparent border-0 cursor-pointer transition-colors hover:bg-surface-25">
                <span className="flex items-center gap-3 min-w-0">
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[15px] ${tone('brand').chip}`}>
                        <ChartColumnStacked />
                    </span>
                    <span className="m-0 text-[15px] font-semibold text-surface-900 truncate">Share of Shelf</span>
                    <StatusTag className="tabular-nums">{categoryList.length} categorías</StatusTag>
                </span>
                <ChevronDown className={`text-base text-surface-400 transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`} />
            </button>

            {/* Content */}
            {isExpanded && (
                <div className="px-5 pb-5 pt-4 border-t border-surface-100">
                    {/* Toolbar */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                        <span className="text-sm text-surface-500">Comparativa de caras en anaquel</span>
                        <div className="flex items-center gap-2">
                            <Select
                                value={filter}
                                className="w-[200px]"
                                onChange={(e) => setFilter(e)}
                                options={[{ value: 'all', label: 'Todas las categorías' }, ...categoryList?.map((c) => ({ value: c.name, label: c.value }))]}
                            />
                            <Button icon={<Download />} onClick={download}>
                                Exportar
                            </Button>
                        </div>
                    </div>

                    {/* Chart */}
                    <div className="h-[400px]">
                        <canvas ref={chartRef} />
                    </div>
                </div>
            )}
        </section>
    );
}
